import React, { useEffect, useState } from 'react';
import { useLayoutSpacing } from '@/hooks/useSafeArea';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Crown } from 'lucide-react';

interface ToastProps {
  message: string;
  isVisible: boolean;
  type?: 'success' | 'error' | 'info';
  onClose?: () => void;
}

export default function Toast({ message, isVisible, type = 'success', onClose }: ToastProps) {
  const { topBarHeight } = useLayoutSpacing();
  const [shouldRender, setShouldRender] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
    } else {
      // Delay para permitir a animação de saída
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  const getToastStyles = () => {
    switch (type) {
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'info':
        return 'bg-blue-100 text-blue-800';
      case 'success':
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const handlePremiumClick = () => {
    if (onClose) onClose();
    navigate('/settings?section=premium');
    
    // Aguarda a navegação e então faz o scroll
    setTimeout(() => {
      const premiumCard = document.getElementById('premium-card');
      if (premiumCard) {
        premiumCard.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100);
  };

  const isPremiumMessage = message === 'Esta música está disponível apenas para usuários premium';

  return (
    <div
      className={cn(
        'fixed left-1/2 transform -translate-x-1/2 z-[9999]',
        'w-full max-w-[90%] md:max-w-[400px]',
        'flex flex-col items-center',
        'rounded-xl overflow-hidden',
        'shadow-lg',
        'transition-all duration-300 ease-in-out',
        isPremiumMessage ? 'bg-white border border-amber-200' : getToastStyles(),
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 -translate-y-full'
      )}
      style={{
        top: `${topBarHeight + 16}px`, // altura do TopBar + espaçamento
      }}
    >
      <div className="w-full px-4 py-3 text-center">
        <span className={cn(
          "text-sm font-medium leading-tight",
          isPremiumMessage ? "text-gray-800" : ""
        )}>
          {message}
        </span>
      </div>
      
      {isPremiumMessage && (
        <div className="w-full px-4 pb-3">
          <button
            onClick={handlePremiumClick}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 
                     text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 
                     flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
          >
            <Crown className="w-4 h-4" />
            Tornar-se Premium
          </button>
        </div>
      )}
    </div>
  );
} 